/* Continuous scan loop.
 *
 * The owner requirement is back-to-back bulk scanning: a supplier at a warehouse
 * works down a pallet, one module after another, and the flow must not be
 * "scan, dismiss the sheet, tap scan again". So this is a loop that keeps
 * running, keeps a list, and gives feedback per read — not a one-shot promise.
 *
 * Two suppression mechanisms, doing different jobs:
 *
 *   - The SESSION suppresses duplicate serials and counts them (scanSession.ts).
 *     That count is a real number the technician should see.
 *   - The LOOP suppresses repeat *events* for the same serial inside a short
 *     cooldown, silently. Without it a label sitting under the lens produces ~15
 *     "already scanned" events per second, which drowns the feedback line and
 *     inflates the duplicate count into nonsense. Those reads are the same
 *     physical read, not a re-scan.
 *
 * Everything external is injected: grab a frame, decode a frame, tell the time,
 * wait, mint an id. A browser camera cannot be unit-tested; this loop can, and
 * is. What is NOT tested here is the frame grab itself (see `videoFrameGrabber`).
 */

import { offer } from './scanSession';
import {
  DEFAULT_SERIAL_RULES,
  type ScanEvent,
  type ScanSessionState,
  type SerialRules,
} from './types';
import type { DecodeCandidate } from './decodeFilter';

export interface ScanLoopPorts {
  /** One frame of pixels, or null if the video is not producing frames yet. */
  grabFrame(): Promise<ImageData | null>;
  decode(frame: ImageData): Promise<DecodeCandidate[]>;
  now(): number;
  delay(ms: number): Promise<void>;
  newId(): string;
}

export interface ScanLoopOptions {
  /** Minimum gap between decode attempts. ~10/s is plenty and saves battery. */
  intervalMs: number;
  /** Window in which a repeat read of the same serial is treated as one read. */
  cooldownMs: number;
  rules: SerialRules;
}

export const DEFAULT_LOOP_OPTIONS: ScanLoopOptions = {
  intervalMs: 100,
  cooldownMs: 1200,
  rules: DEFAULT_SERIAL_RULES,
};

export interface ScanLoopHandle {
  /**
   * Resolves when the loop has stopped.
   *
   * Deliberately resolves to `void` rather than to the final session: manual
   * entry stays available after the camera stops (a technician who lost the
   * light finishes the pallet by hand), so any state value captured at stop
   * time would go stale the moment they typed. `state()` is the one accessor,
   * and it is always live.
   */
  readonly done: Promise<void>;
  stop(): void;
  /** Current state — the loop replaces this on every change. */
  state(): ScanSessionState;
  /**
   * Feed a hand-typed serial through the same session as a scan. Exposed on the
   * handle so manual entry stays available while the camera is still running.
   */
  submitManual(raw: string): ScanEvent | null;
}

export interface ScanLoopCallbacks {
  onEvent?(event: ScanEvent, state: ScanSessionState): void;
  onState?(state: ScanSessionState): void;
  /** A decode threw. The loop keeps going; the UI may want to count these. */
  onDecodeError?(err: unknown): void;
}

/**
 * Start scanning. Returns immediately with a handle.
 *
 * The loop never throws out of itself: a decode failure on one frame is normal
 * (motion blur, a hand in shot) and must not end a bulk session halfway down a
 * pallet.
 */
export function startScanLoop(
  initial: ScanSessionState,
  ports: ScanLoopPorts,
  callbacks: ScanLoopCallbacks = {},
  options: ScanLoopOptions = DEFAULT_LOOP_OPTIONS,
): ScanLoopHandle {
  let state = initial;
  let running = true;
  /** serial -> last time we emitted any event for it. */
  const lastSeen = new Map<string, number>();

  const commit = (next: ScanSessionState) => {
    const event = next.lastEvent;
    state = next;
    callbacks.onState?.(state);
    if (event) callbacks.onEvent?.(event, state);
  };

  const push = (raw: string, format: string | null, method: 'scan' | 'manual'): ScanEvent | null => {
    const at = ports.now();
    const next = offer(
      state,
      { raw, format, method, at, id: ports.newId() },
      options.rules,
    );
    if (!next.lastEvent) return null;
    commit(next);
    return next.lastEvent;
  };

  const done = (async () => {
    while (running) {
      let frame: ImageData | null = null;
      try {
        frame = await ports.grabFrame();
      } catch (err) {
        callbacks.onDecodeError?.(err);
      }

      if (frame) {
        try {
          const candidates = await ports.decode(frame);
          const at = ports.now();
          for (const c of candidates) {
            // Cooldown is keyed on the raw text: we have not normalised yet, and
            // the same label yields byte-identical text frame to frame.
            const seen = lastSeen.get(c.raw);
            if (seen !== undefined && at - seen < options.cooldownMs) continue;
            lastSeen.set(c.raw, at);
            push(c.raw, c.format, 'scan');
          }
        } catch (err) {
          callbacks.onDecodeError?.(err);
        }
      }

      if (!running) break;
      await ports.delay(options.intervalMs);
    }
  })();

  return {
    done,
    stop() {
      running = false;
    },
    state: () => state,
    submitManual(raw: string) {
      // Manual entry bypasses the cooldown entirely. If a technician types a
      // serial they already scanned, they deserve to be told it is a duplicate
      // straight away rather than have the input silently swallowed.
      return push(raw, null, 'manual');
    },
  };
}

/* ------------------------------------------------------- frame grab (browser) */

/**
 * Pull ImageData off a live `<video>` via an offscreen canvas.
 *
 * NOT unit-tested — it needs a real video element with a real camera stream
 * behind it. It is deliberately tiny for that reason: all the decisions live in
 * the loop above, which is tested.
 *
 * `downscaleTo` caps the decode resolution. Full 1080p frames are slower to
 * decode than they are more accurate for a label filling a third of the frame,
 * and the phone gets hot.
 */
export function videoFrameGrabber(
  video: HTMLVideoElement,
  downscaleTo = 960,
): () => Promise<ImageData | null> {
  let canvas: HTMLCanvasElement | null = null;
  return async () => {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh || video.readyState < 2) return null;
    const scale = Math.min(1, downscaleTo / Math.max(vw, vh));
    const w = Math.max(1, Math.round(vw * scale));
    const h = Math.max(1, Math.round(vh * scale));
    canvas ??= document.createElement('canvas');
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, w, h);
    return ctx.getImageData(0, 0, w, h);
  };
}

/** Default loop ports for the browser. */
export function browserLoopPorts(
  video: HTMLVideoElement,
  decode: (frame: ImageData) => Promise<DecodeCandidate[]>,
): ScanLoopPorts {
  const grab = videoFrameGrabber(video);
  return {
    grabFrame: grab,
    decode,
    now: () => Date.now(),
    delay: (ms) => new Promise((r) => setTimeout(r, ms)),
    newId: () => newCaptureId(),
  };
}

/** Ids only need to be unique within a session, and to sort by creation. */
export function newCaptureId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cap-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
